import Feature from 'ol/Feature'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { fromExtent } from 'ol/geom/Polygon'
import Point from 'ol/geom/Point'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import type OlMap from 'ol/Map'
import type { Extent } from 'ol/extent'
import type { Coordinate } from 'ol/coordinate'

/**
 * Map highlight layer. Two uses:
 *  - `showBbox` — a rectangle from a guided-SQL row's bbox_3857 (PRD Phase 2).
 *  - `showPoint` — a marker at the clicked location (T2), anchoring the selection
 *    whose attributes are shown in the right "Details" panel. GetFeatureInfo
 *    returns no geometry, so we mark the click point rather than the feature.
 */
export interface HighlightLayer {
  showBbox(bbox: [number, number, number, number]): void
  showPoint(coordinate: Coordinate): void
  clear(): void
}

const HIGHLIGHT_STYLE = new Style({
  stroke: new Stroke({ color: '#c92a2a', width: 2.5 }),
  fill: new Fill({ color: 'rgba(201, 42, 42, 0.08)' }),
})

const POINT_STYLE = new Style({
  image: new CircleStyle({
    radius: 7,
    fill: new Fill({ color: 'rgba(201, 42, 42, 0.85)' }),
    stroke: new Stroke({ color: '#ffffff', width: 2 }),
  }),
})

export function createHighlight(map: OlMap): HighlightLayer {
  const source = new VectorSource()
  const layer = new VectorLayer({ source, style: HIGHLIGHT_STYLE, zIndex: 25 })
  map.addLayer(layer)
  return {
    showBbox(bbox) {
      source.clear()
      source.addFeature(new Feature(fromExtent(bbox as Extent)))
      map.getView().fit(bbox as Extent, { padding: [60, 60, 60, 60], maxZoom: 14, duration: 400 })
    },
    showPoint(coordinate) {
      source.clear()
      const marker = new Feature(new Point(coordinate))
      marker.setStyle(POINT_STYLE) // per-feature: overrides the layer's polygon style
      source.addFeature(marker)
    },
    clear() {
      source.clear()
    },
  }
}
